/**
 * AdminInvoiceVerifyPage — Internal staff invoice verifier.
 *
 * Accessible to STAS, GM, Deputy, Accountant, Employees. Two input paths:
 *   1) Type the invoice ref or application number.
 *   2) Tap "Scan QR" → opens device camera (html5-qrcode) → reads the QR
 *      printed on the receipt → extracts ?ref= & ?hash= → verifies.
 *
 * Both paths call the SAME `/api/admin/invoices/verify` endpoint which logs
 * every verify attempt in the audit trail.
 */
import { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldOff, Camera, Search, X, Loader2, Pencil, Ban, Undo2, History } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";

const ACTION_ROLES = new Set(["general_manager", "deputy_manager", "accountant"]);

const STATUS_META = {
  valid:     { icon: ShieldCheck, color: "#16a34a", bg: "rgba(22,163,74,0.12)", label: "فاتورة أصلية ومعتمدة" },
  tampered:  { icon: ShieldAlert, color: "#dc2626", bg: "rgba(220,38,38,0.12)", label: "⚠️ فاتورة معدّلة خارج النظام" },
  cancelled: { icon: ShieldOff,   color: "#dc2626", bg: "rgba(220,38,38,0.10)", label: "ملغاة" },
  refunded:  { icon: ShieldOff,   color: "#d4af37", bg: "rgba(212,175,55,0.12)", label: "مستردّة" },
  not_found: { icon: ShieldOff,   color: "#f59e0b", bg: "rgba(245,158,11,0.10)", label: "لم يتم العثور على الفاتورة" },
};

export default function AdminInvoiceVerifyPage() {
  const { theme, dir } = useI18n();
  const { user } = useAuth();
  const isInvoiceAdmin = !!user?.role?.is_root || ACTION_ROLES.has(user?.role?.code || "");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "vta-qr-reader";

  const submitQuery = async (q, providedHash) => {
    if (!q?.trim()) { setErr("أدخل رقم المعاملة أو الفاتورة"); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const isInv = q.toUpperCase().startsWith("INV-");
      const params = new URLSearchParams();
      if (isInv) params.set("ref", q); else params.set("application_number", q);
      if (providedHash) params.set("hash", providedHash);
      const { data } = await api.get(`/admin/invoices/verify?${params.toString()}`);
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startScan = async () => {
    setErr(""); setResult(null);
    setScanning(true);
    // Wait a tick for the container div to mount, then start the camera
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          async (decoded) => {
            // decoded text is the verify URL: ".../verify?ref=...&hash=..."
            try { await scanner.stop(); } catch { /* ignore */ }
            setScanning(false);
            const u = new URL(decoded.startsWith("http") ? decoded : `https://x${decoded}`);
            const ref = u.searchParams.get("ref");
            const hash = u.searchParams.get("hash");
            const appn = u.searchParams.get("application_number");
            if (ref || appn) {
              setInput(ref || appn);
              await submitQuery(ref || appn, hash);
            } else {
              setErr("رمز QR غير صالح — لا يحتوي على بيانات فاتورة");
            }
          },
          () => { /* per-frame parse errors are noisy — ignore */ }
        );
      } catch (e) {
        setErr(`تعذّر تشغيل الكاميرا: ${e?.message || e}`);
        setScanning(false);
      }
    }, 60);
  };

  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch { /* ignore */ }
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); /* cleanup on unmount */ }, []);

  const css = makeStyles(theme);

  return (
    <div dir={dir} style={css.page} data-testid="admin-invoice-verify-page">
      <header style={css.header}>
        <h1 style={css.h1}>التحقق من الفواتير</h1>
        <p style={css.sub}>تحقق من أصالة أي فاتورة عن طريق رقم المعاملة أو مسح رمز QR المطبوع. كل عملية تحقق تُسجَّل تلقائياً في سجل التدقيق.</p>
      </header>

      <div style={css.card}>
        <form onSubmit={(e) => { e.preventDefault(); submitQuery(input); }} style={css.form}>
          <div style={css.inputRow}>
            <Search size={16} strokeWidth={2.2} style={{ color: theme.textMuted }} />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              placeholder="VTA-26-XXXX  أو  INV-2026-000001"
              style={css.input}
              dir="ltr"
              data-testid="admin-verify-input"
            />
          </div>
          <button type="submit" disabled={busy} style={css.btnGold} data-testid="admin-verify-search-btn">
            {busy ? <Loader2 size={16} className="vta-spin" /> : <Search size={16} strokeWidth={2.4} />}
            تحقق
          </button>
          <button type="button" onClick={scanning ? stopScan : startScan} style={css.btnGhost} data-testid="admin-verify-scan-btn">
            {scanning ? <X size={16} strokeWidth={2.4} /> : <Camera size={16} strokeWidth={2.4} />}
            {scanning ? "إيقاف" : "مسح QR"}
          </button>
        </form>

        {scanning && (
          <div style={{ marginTop: 16, borderRadius: 12, overflow: "hidden", background: "#000" }}>
            <div id={containerId} style={{ width: "100%" }} />
            <div style={{ padding: 10, fontSize: 11, color: theme.textMuted, textAlign: "center" }}>
              وجّه الكاميرا نحو رمز QR المطبوع على الإيصال
            </div>
          </div>
        )}

        {err && <div style={css.err} data-testid="admin-verify-error">{err}</div>}

        {result && (
          <AdminResultCard
            result={result}
            theme={theme}
            isInvoiceAdmin={isInvoiceAdmin}
            onActionDone={() => submitQuery(input)}
          />
        )}
      </div>

      <style>{`.vta-spin{animation:vta-spin 1s linear infinite}@keyframes vta-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function AdminResultCard({ result, theme, isInvoiceAdmin, onActionDone }) {
  const meta = STATUS_META[result.status] || STATUS_META.not_found;
  const Icon = meta.icon;
  const [modal, setModal] = useState(null);   // 'cancel' | 'refund' | 'revise' | 'audit'
  const isTerminated = result.invoice_status === "cancelled" || result.invoice_status === "refunded";
  const canAct = isInvoiceAdmin && !!result.application_number && !isTerminated && result.status !== "not_found";
  return (
    <div data-testid={`admin-verify-result-${result.status}`}>
      <div style={{
        background: meta.bg, color: meta.color, borderRadius: 14,
        padding: "16px 18px", display: "flex", alignItems: "center",
        gap: 12, fontWeight: 900, fontSize: 16, marginTop: 18,
        border: `2px solid ${meta.color}`,
      }}>
        <Icon size={24} strokeWidth={2.4} />
        <span>{meta.label}</span>
      </div>
      {(result.status === "valid" || result.status === "tampered" || result.status === "cancelled" || result.status === "refunded") && (
        <div style={{ marginTop: 16, padding: 16, background: theme.surfaceSubtle, borderRadius: 12, border: `1px solid ${theme.borderSoft}` }}>
          <Row label="رقم المعاملة" value={result.application_number} ltr />
          <Row label="رقم الفاتورة" value={result.invoice_ref_number} ltr />
          <Row label="اسم العميل" value={result.client_name} />
          <Row label="الدولة" value={result.country} />
          <Row label="نوع التأشيرة" value={result.visa_type} />
          <Row label="الإجمالي" value={`${fmtNum(result.final_total)} ${result.currency}`} ltr bold />
          {result.discount_amount > 0 && (
            <Row label="الخصم" value={`${fmtNum(result.discount_amount)} ${result.currency}`} ltr />
          )}
          <Row label="حالة الفاتورة" value={result.invoice_status} />
          {result.revision > 0 && (
            <Row label="رقم المراجعة" value={`Rev.${result.revision}`} ltr />
          )}
          {result.issued_at && (
            <Row label="تاريخ الإصدار" value={new Date(result.issued_at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })} ltr />
          )}
          <Row label="بصمة الفاتورة (Hash)" value={(result.expected_hash || "").slice(0, 32) + "…"} ltr />
          {result.expected_hash !== result.computed_hash && (
            <div style={{ background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 8, borderRadius: 8, fontSize: 11, marginTop: 8, fontFamily: "monospace" }}>
              expected: {result.expected_hash} <br /> got: {result.computed_hash}
            </div>
          )}
          {isInvoiceAdmin && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14, borderTop: `1px solid ${theme.borderSoft}`, paddingTop: 12 }}>
              {canAct && (
                <>
                  <button onClick={() => setModal("revise")} style={btnAction("#0ea5e9", theme)} data-testid="invoice-action-revise">
                    <Pencil size={14} strokeWidth={2.4} /> إنشاء مراجعة
                  </button>
                  <button onClick={() => setModal("refund")} style={btnAction("#d4af37", theme)} data-testid="invoice-action-refund">
                    <Undo2 size={14} strokeWidth={2.4} /> استرجاع
                  </button>
                  <button onClick={() => setModal("cancel")} style={btnAction("#dc2626", theme)} data-testid="invoice-action-cancel">
                    <Ban size={14} strokeWidth={2.4} /> إلغاء الفاتورة
                  </button>
                </>
              )}
              <button onClick={() => setModal("audit")} style={btnAction(theme.textMuted, theme)} data-testid="invoice-action-audit">
                <History size={14} strokeWidth={2.4} /> سجل التدقيق
              </button>
            </div>
          )}
        </div>
      )}
      {modal && (
        <ActionModal
          kind={modal}
          appNum={result.application_number}
          currentItems={result.items}
          currentDiscount={result.discount_amount}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); onActionDone?.(); }}
        />
      )}
    </div>
  );
}

function btnAction(color, theme) {
  return {
    background: "transparent",
    color,
    border: `1.5px solid ${color}`,
    padding: "8px 14px",
    borderRadius: 10,
    fontWeight: 800,
    fontSize: 12,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
}

function ActionModal({ kind, appNum, currentItems = [], currentDiscount = 0, onClose, onSuccess }) {
  const { theme } = useI18n();
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [items, setItems] = useState(currentItems.map((i) => ({ label: i.label, amount: i.amount, qty: 1, is_office_revenue: !!i.is_office_revenue })));
  const [discountAmount, setDiscountAmount] = useState(currentDiscount || 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [audit, setAudit] = useState(null);

  // For "audit", we only need to fetch and display
  useEffect(() => {
    if (kind !== "audit") return;
    setBusy(true);
    api.get(`/admin/invoices/${appNum}/audit`)
      .then(({ data }) => setAudit(data.entries || []))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setBusy(false));
  }, [kind, appNum]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (kind === "cancel") {
        await api.post(`/admin/invoices/${appNum}/cancel`, { reason });
      } else if (kind === "refund") {
        await api.post(`/admin/invoices/${appNum}/refund`, { reason, amount: parseFloat(amount) || 0 });
      } else if (kind === "revise") {
        await api.post(`/admin/invoices/${appNum}/revise`, {
          reason,
          items: items.map((i) => ({ label: i.label, amount: parseFloat(i.amount) || 0, qty: i.qty || 1, is_office_revenue: !!i.is_office_revenue })),
          discount_amount: parseFloat(discountAmount) || 0,
        });
      }
      onSuccess();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    cancel: "إلغاء الفاتورة",
    refund: "استرجاع مبلغ",
    revise: "إنشاء مراجعة جديدة",
    audit: "سجل التدقيق",
  };

  return (
    <div style={modalCss.backdrop} onClick={onClose} data-testid={`invoice-modal-${kind}`}>
      <div style={modalCss.dialog(theme)} onClick={(e) => e.stopPropagation()}>
        <div style={modalCss.head(theme)}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: theme.gold }}>{titles[kind]}</h3>
          <button onClick={onClose} style={modalCss.x(theme)}><X size={16} /></button>
        </div>
        <div style={{ padding: 16, maxHeight: "75vh", overflowY: "auto" }}>
          {kind === "audit" ? (
            <AuditList busy={busy} entries={audit} theme={theme} />
          ) : (
            <>
              {kind === "revise" && (
                <div style={{ marginBottom: 14 }}>
                  <label style={modalCss.lbl(theme)}>البنود الجديدة</label>
                  {items.map((it, i) => (
                    <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input value={it.label} onChange={(e) => setItems((s) => s.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} placeholder="اسم البند" style={modalCss.input(theme)} />
                      <input value={it.amount} onChange={(e) => setItems((s) => s.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} placeholder="المبلغ" inputMode="decimal" style={{ ...modalCss.input(theme), maxWidth: 100 }} />
                      <button onClick={() => setItems((s) => s.filter((_, idx) => idx !== i))} style={{ ...btnAction("#dc2626", theme), padding: "4px 8px" }}><X size={12} /></button>
                    </div>
                  ))}
                  <button onClick={() => setItems((s) => [...s, { label: "", amount: 0, qty: 1, is_office_revenue: false }])} style={{ ...btnAction(theme.gold, theme), marginTop: 4 }}>+ بند جديد</button>
                  <label style={{ ...modalCss.lbl(theme), marginTop: 12 }}>قيمة الخصم</label>
                  <input value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} inputMode="decimal" style={modalCss.input(theme)} />
                </div>
              )}
              {kind === "refund" && (
                <div style={{ marginBottom: 14 }}>
                  <label style={modalCss.lbl(theme)}>مبلغ الاسترجاع (ر.س)</label>
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" style={modalCss.input(theme)} data-testid="modal-refund-amount" />
                </div>
              )}
              <label style={modalCss.lbl(theme)}>السبب (إلزامي — على الأقل 4 أحرف)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                style={modalCss.input(theme)}
                placeholder="اشرح سبب العملية بوضوح"
                data-testid={`modal-${kind}-reason`}
              />
              {err && <div style={modalCss.err}>{err}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
                <button onClick={onClose} style={btnAction(theme.textMuted, theme)}>إلغاء</button>
                <button onClick={submit} disabled={busy || reason.trim().length < 4} style={{ ...btnAction(theme.gold, theme), opacity: (busy || reason.trim().length < 4) ? 0.5 : 1 }} data-testid={`modal-${kind}-submit`}>
                  {busy ? <Loader2 size={14} className="vta-spin" /> : null}
                  تأكيد
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditList({ busy, entries, theme }) {
  if (busy) return <div style={{ textAlign: "center", padding: 20, color: theme.textMuted }}>جاري التحميل…</div>;
  if (!entries || entries.length === 0) return <div style={{ textAlign: "center", padding: 20, color: theme.textMuted }}>لا توجد عمليات مسجّلة بعد.</div>;
  const labels = { issue: "إصدار", verify: "تحقق", revise: "مراجعة", cancel: "إلغاء", refund: "استرجاع" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="invoice-audit-list">
      {entries.map((e, i) => (
        <div key={i} style={{ padding: 10, background: theme.surfaceSubtle, borderRadius: 8, fontSize: 12, border: `1px solid ${theme.borderSoft}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <strong style={{ color: theme.gold }}>{labels[e.action] || e.action}</strong>
            <span style={{ fontSize: 10, color: theme.textMuted, fontFamily: "monospace" }} dir="ltr">
              {new Date(e.at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })}
            </span>
          </div>
          {e.reason && <div style={{ fontSize: 11, color: theme.text }}>السبب: {e.reason}</div>}
          {e.refund_amount && <div style={{ fontSize: 11, color: theme.text }}>المبلغ: {fmtNum(e.refund_amount)} ر.س</div>}
          {e.new_revision !== undefined && <div style={{ fontSize: 11, color: theme.text }}>الإصدار الجديد: Rev.{e.new_revision}</div>}
          {e.actor_role && <div style={{ fontSize: 10, color: theme.textMuted }}>المنفذ: {e.actor_role}</div>}
        </div>
      ))}
    </div>
  );
}

const modalCss = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  dialog: (theme) => ({ width: "100%", maxWidth: 480, background: theme.surface, borderRadius: 16, border: `1px solid ${theme.borderSoft}`, overflow: "hidden" }),
  head: (theme) => ({ padding: "12px 16px", borderBottom: `1px solid ${theme.borderSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center" }),
  x: (theme) => ({ background: "transparent", border: "none", color: theme.textMuted, cursor: "pointer" }),
  lbl: (theme) => ({ display: "block", fontSize: 11, fontWeight: 700, color: theme.textMuted, marginBottom: 4, marginTop: 4 }),
  input: (theme) => ({ width: "100%", padding: "8px 10px", borderRadius: 8, background: theme.surfaceSubtle, border: `1px solid ${theme.borderSoft}`, color: theme.text, fontSize: 13, outline: "none", boxSizing: "border-box" }),
  err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 8, borderRadius: 8, fontSize: 11, marginTop: 8 },
};

function Row({ label, value, ltr, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed rgba(0,0,0,0.06)", gap: 8 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>{label}</span>
      <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 900 : 600, direction: ltr ? "ltr" : "rtl" }}>{value || "—"}</span>
    </div>
  );
}

function makeStyles(theme) {
  return {
    page: { maxWidth: 720, margin: "0 auto", padding: 18, color: theme.text },
    header: { marginBottom: 18 },
    h1: { fontSize: 22, fontWeight: 900, color: theme.gold, margin: 0 },
    sub: { fontSize: 12, color: theme.textMuted, marginTop: 6, lineHeight: 1.7, maxWidth: 600 },
    card: { background: theme.surface, padding: 22, borderRadius: 16, border: `1px solid ${theme.borderSoft}` },
    form: { display: "flex", gap: 8, flexWrap: "wrap" },
    inputRow: { flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: theme.surfaceSubtle, borderRadius: 10, border: `1px solid ${theme.borderSoft}` },
    input: { flex: 1, border: "none", background: "transparent", color: theme.text, fontSize: 14, fontFamily: "monospace", outline: "none" },
    btnGold: { background: theme.gold, color: "#0a0a0a", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
    btnGhost: { background: "transparent", color: theme.text, border: `1px solid ${theme.borderSoft}`, padding: "10px 14px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
    err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 10, borderRadius: 8, fontSize: 12, marginTop: 12 },
  };
}
